/**
 * Console Filter for GeoComply SDK
 * 
 * GeoComply SDK intentionally makes requests to random localhost ports as part of
 * its anti-fraud/spoofing detection mechanism. These requests fail with ERR_CONNECTION_REFUSED,
 * which is expected behavior and does not indicate a malfunction.
 * 
 * This utility filters out these expected console errors to reduce noise in development.
 * 
 * Reference: GeoComply Developer Documentation - "Developer console errors"
 * https://clientportal.geocomply.com/docs/docs-core-ndbs/integrate/error-codes-messages/dev-console-errors
 */

/**
 * Checks if an error message is from GeoComply's expected localhost connection attempts
 */
function isGeoComplyExpectedError(message: string): boolean {
  // Pattern: GET https://localhost:PORT/NUMBER.png net::ERR_CONNECTION_REFUSED
  const geocomplyLocalhostPattern = /^GET https:\/\/localhost:\d+\/\d+\.png net::ERR_CONNECTION_REFUSED$/;
  
  // Pattern: localhost:PORT/NUMBER.png:1 GET https://localhost:PORT/NUMBER.png net::ERR_CONNECTION_REFUSED
  const geocomplyLocalhostWithLinePattern = /localhost:\d+\/\d+\.png.*GET https:\/\/localhost:\d+\/\d+\.png net::ERR_CONNECTION_REFUSED/;
  
  // Pattern: Just the filename with localhost port (from stack traces)
  const geocomplyLocalhostFilenamePattern = /localhost:\d+\/\d+\.png/;
  
  // Pattern: ERR_CONNECTION_REFUSED with localhost and .png
  const geocomplyConnectionRefusedPattern = /ERR_CONNECTION_REFUSED.*localhost.*\.png/;
  
  return (
    geocomplyLocalhostPattern.test(message) ||
    geocomplyLocalhostWithLinePattern.test(message) ||
    (geocomplyLocalhostFilenamePattern.test(message) && geocomplyConnectionRefusedPattern.test(message))
  );
}

/**
 * Sets up console error filtering for GeoComply expected errors
 * 
 * This should be called early in the application lifecycle (e.g., in main.tsx)
 * to filter out expected GeoComply SDK connection errors.
 * 
 * IMPORTANT NOTE:
 * - This filter will suppress errors logged via console.error() and console.warn()
 * - However, browser network errors logged directly by the browser's network layer
 *   (visible in the Network tab and console) cannot be filtered programmatically
 * - These network errors are harmless and expected - they're part of GeoComply's
 *   anti-fraud detection mechanism
 * 
 * @param options - Configuration options
 * @param options.suppressInProduction - Whether to suppress errors in production (default: true)
 *   Set to false if you want to see these errors in production for debugging
 */
export function setupGeoComplyConsoleFilter(options: { suppressInProduction?: boolean } = {}): void {
  const { suppressInProduction = true } = options;
  
  // Check if we should filter in current environment
  const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;
  const isDevelopment = import.meta.env.MODE === 'development';
  
  // Filter logic:
  // - Always filter in development
  // - Filter in production only if suppressInProduction is true
  // Don't filter if we're in production and suppression is disabled
  if (isProduction && !suppressInProduction) {
    return;
  }

  // Store original console methods
  const originalError = console.error;
  const originalWarn = console.warn;
  
  // Expose original methods on console object for debugging
  (console as any)._originalError = originalError;
  (console as any)._originalWarn = originalWarn;
  (console as any)._originalLog = console.log;

  // Override console.error to filter GeoComply expected errors
  console.error = (...args: any[]) => {
    // Check if any of the arguments contain a GeoComply expected error
    const message = args.map(arg => 
      typeof arg === 'string' ? arg : 
      typeof arg === 'object' ? JSON.stringify(arg) : 
      String(arg)
    ).join(' ');
    
    if (isGeoComplyExpectedError(message)) {
      // Suppress this error - it's expected behavior from GeoComply SDK
      // Optionally, you can log it at a lower level if needed for debugging
      // console.debug('[GeoComply] Expected connection error (suppressed):', message);
      return;
    }
    
    // For all other errors, use the original console.error
    originalError.apply(console, args);
  };

  // Also filter warnings that might be GeoComply related
  console.warn = (...args: any[]) => {
    const message = args.map(arg => 
      typeof arg === 'string' ? arg : 
      typeof arg === 'object' ? JSON.stringify(arg) : 
      String(arg)
    ).join(' ');
    
    if (isGeoComplyExpectedError(message)) {
      // Suppress GeoComply expected warnings
      return;
    }
    
    originalWarn.apply(console, args);
  };
}

